import React, { useRef, useEffect, useState } from "react";
import Webcam from "react-webcam";
import jsQR from "jsqr";
import { useLazyCodart } from "../hooks";
import { useDebounce } from "../../../hooks/useDebounce";
import useWindowDimensions from "../../../hooks/useWindowDimensions";

const CameraScanner = () => {
  const { height: displayHeight, width: displayWidth } = useWindowDimensions();

  React.useEffect(() => {
    const handleContextMenu = (e: Event) => e.preventDefault();
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 1) e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("touchstart", handleTouchStart);
    };
  }, []);

  const webcamRef = useRef(null);
  const [imgUrl, setImgUrl] = useState("");
  const [code, setCode] = useState("");
  const [isQRCodeDetected, setIsQRCodeDetected] = useState(0);
  const debouncedQRCodeDetected = useDebounce(isQRCodeDetected, 500);

  const [isLongPress, setIsLongPress] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const LONG_PRESS_DURATION = 500;

  React.useEffect(() => {
    if (isLongPress) {
      console.log("Appui long détecté !");
      setImgUrl(data?.codart?.result?.hiddenLink ?? "");
    } else {
      setImgUrl(data?.codart?.result?.publicLink ?? "");
    }
  }, [isLongPress]);

  // Début de l'appui
  const handleTouchStart = () => {
    timeoutRef.current = setTimeout(() => {
      setIsLongPress(true);
      console.log("Appui long détecté !");
    }, LONG_PRESS_DURATION);
  };

  // Fin de l'appui
  const handleTouchEnd = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsLongPress(false);
  };

  // Annuler si le doigt glisse
  const handleTouchMove = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsLongPress(false);
  };

  useEffect(() => {
    if (debouncedQRCodeDetected) {
      setIsQRCodeDetected(0);
    }
  }, [debouncedQRCodeDetected]);

  // États pour la position et la taille de l'image avec transition fluide
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    rotation: 0,
  });

  const [getCodart, { data, loading }] = useLazyCodart();
  // useEffect(() => {
  //   if (data?.codart?.result?.code === code) return;
  //   setImgUrl(
  //     isLongPress
  //       ? data?.codart?.result?.hiddenLink ?? ""
  //       : data?.codart?.result?.publicLink ?? ""
  //   );
  //   setCode(data?.codart?.result?.code ?? "");
  // }, [data, isLongPress, code]);

  React.useEffect(() => {
    if (data?.codart?.result?.code === code) {
      return;
    } else {
      if (isLongPress) {
        setImgUrl(data?.codart?.result?.hiddenLink ?? "");
      } else {
        setImgUrl(data?.codart?.result?.publicLink ?? "");
      }
      setCode(data?.codart?.result?.code ?? "");
    }
  });

  useEffect(() => {
    const interval = setInterval(scanQRCode, 150);
    return () => clearInterval(interval);
  }, []);

  const scanQRCode = () => {
    if (webcamRef.current) {
      // @ts-ignore
      const video = webcamRef?.current?.video;
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
        let qr = imageData
          ? jsQR(imageData.data, imageData.width, imageData.height)
          : null;

        if (qr) {
          setIsQRCodeDetected((prev) => prev + 1);
          const extractedCode = qr?.data?.split("code=")[1] || null;
          if (extractedCode && !loading) {
            getCodart({ variables: { code: extractedCode } });
          }

          const scaleX =
            // @ts-ignore
            webcamRef.current?.video?.clientWidth / video.videoWidth;
          const scaleY =
            // @ts-ignore
            webcamRef.current?.video?.clientHeight / video.videoHeight;

          if (qr.location) {
            const { topLeftCorner, topRightCorner, bottomLeftCorner } =
              qr.location;
            let w =
              Math.sqrt(
                Math.pow(topRightCorner.x - topLeftCorner.x, 2) +
                  Math.pow(topRightCorner.y - topLeftCorner.y, 2)
              ) * 1.2;
            let h =
              Math.sqrt(
                Math.pow(bottomLeftCorner.x - topLeftCorner.x, 2) +
                  Math.pow(bottomLeftCorner.y - topLeftCorner.y, 2)
              ) * 1.2;
            let rotation =
              Math.atan2(
                topRightCorner.y - topLeftCorner.y,
                topRightCorner.x - topLeftCorner.x
              ) *
              (180 / Math.PI);

            setPosition((prev) => ({
              top: (topLeftCorner.y - h * 0.1) * scaleY,
              left: (topLeftCorner.x - w * 0.1) * scaleX,
              width: w * scaleX,
              height: h * scaleY,
              rotation,
            }));
          }
        }
      }
    }
  };

  return (
    <>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <Webcam
          audio={false}
          ref={webcamRef}
          width={displayWidth}
          height={displayHeight}
          videoConstraints={{
            facingMode: { exact: "environment" },
            aspectRatio: displayHeight / displayWidth,
          }}
        />
        {isQRCodeDetected > 0 ? (
          <img
            src={imgUrl}
            alt="Overlay"
            style={{
              position: "absolute",
              top: position.top,
              left: position.left,
              width: position.width,
              height: position.height,
              transform: `rotate(${position.rotation}deg)`,
              transformOrigin: "top left",
              pointerEvents: "none",
              transition:
                "top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease, transform 0.2s ease",
            }}
          />
        ) : null}

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "40px",
            display: "flex",
            paddingTop: "10px",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "black",
          }}
        >
          <p
            style={{
              fontFamily: "Metropolis, Arial, sans-serif",
              color: "white",
              fontSize: "18px",
              margin: 0,
            }}
          >
            @scalance
          </p>
        </div>
      </div>
      <div
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onTouchMove={handleTouchMove}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          color: "white",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          touchAction: "none",
        }}
      ></div>
    </>
  );
};

export default CameraScanner;
